import CustomPickerDay from '@components/commons/DateRangePicker/CustomPickerDate';
import { ButtonBase } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DatePattern } from '@utils/constants';
import { DASHBOARD_TAB } from '@utils/constants/enums';
import { formatDate } from '@utils/helpers';
import moment from 'moment';
import { FC, useMemo, useState } from 'react';
import './ChartDate.scss';

interface IDateRangePickerProps {
  type: string;
  startDate?: string;
  endDate?: string;
  format?: string;
  width?: string;
  onChange: (start: string, end: string) => void;
}

const ChartDate: FC<IDateRangePickerProps> = ({
  type,
  startDate,
  endDate,
  width,
  format = DatePattern.DATE_TIME,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<DateRangeValue>({
    start: startDate ? moment(startDate) : null,
    end: endDate ? moment(endDate) : null,
  });

  const valueText = useMemo(() => {
    let text = '';
    if (value.start) {
      text += formatDate(value.start, format);
      if (value.end && type === DASHBOARD_TAB.WEEK) {
        text += ' - ' + formatDate(value.end, format);
      }
    }
    return text;
  }, [value, type]);

  const handleSelect = (date: moment.Moment) => {
    if (type === DASHBOARD_TAB.DAY) {
      value.start = date;
      value.end = date;
    } else {
      const dateEnd = moment(date);
      const result = dateEnd.add(6, 'days');
      value.start = date;
      value.end = result;
    }
    onChange?.(
      formatDate(value.start, DatePattern.DATE),
      formatDate(value.end, DatePattern.DATE)
    );
    setOpen(false);
    setValue({ ...value });
  };

  const handleClose = () => {
    if (!value.end) {
      setValue({ start: null, end: null });
      onChange('', '');
    }
    setOpen(false);
  };

  return (
    <ButtonBase
      className='ChartDatePicker'
      disableTouchRipple
      disabled={open}
      onClick={() => setOpen(true)}
    >
      <DatePicker
        closeOnSelect={false}
        open={open}
        views={['year', 'month', 'day']}
        slotProps={{
          textField: {
            className: valueText ? 'MuiFormControl-filled' : '',
            placeholder: valueText,
            value: null,
            sx: {
              width: width ?? 'fit-content',
              '& input::placeholder': {
                fontSize: '1.2rem',
              },
            },
            InputProps: {
              startAdornment: null,
              endAdornment: null,
            },
          },
          toolbar: { hidden: true },
          actionBar: { actions: [] },
        }}
        slots={{
          day: pickerProps => (
            <CustomPickerDay
              {...pickerProps}
              range={value}
              onClick={() => handleSelect(pickerProps.day)}
            />
          ),
        }}
        onClose={handleClose}
      />
    </ButtonBase>
  );
};

export default ChartDate;
